import './styles.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spinner } from 'reactstrap';

import {
  findSimilarCigarsDebug,
  fingerprintCigarImage,
  hydrateCigarCandidates,
  hydrateSimilarityCandidates,
  identifyCigarDebug,
  preprocessCigarImage,
  recognizeCigarFingerprint,
  recognizeCigarText,
} from '@/api/cigar-matching';
import Button from '@/components/overrides/button';

export type MatchFlowSourceType =
  | 'match_request'
  | 'recognition_test'
  | 'upload';

export interface MatchFlowSource {
  image: string;
  originalImage?: string;
  sourceType: MatchFlowSourceType;
  sourceId?: string | number;
  existingMatch?: unknown;
}

interface MatchFlowModalProps {
  isOpen: boolean;
  source: MatchFlowSource | null;
  onClose: () => void;
}

type StepKey =
  | 'image'
  | 'preprocess'
  | 'similar'
  | 'text'
  | 'fingerprint'
  | 'fingerprintRecognition'
  | 'identify';
type StepStatus = 'idle' | 'processing' | 'complete' | 'skipped' | 'failed';
type StageState = {
  status: StepStatus;
  result?: any;
  error?: string;
};

const STEPS: Array<{
  key: StepKey;
  label: string;
  cost: string;
}> = [
  { key: 'image', label: 'Image', cost: 'Source' },
  { key: 'preprocess', label: 'Isolate', cost: 'Free' },
  { key: 'similar', label: 'Similar', cost: 'Free · Embeddings' },
  { key: 'text', label: 'Text', cost: 'Paid · Vision' },
  { key: 'fingerprint', label: 'Fingerprint', cost: 'Paid · Vision' },
  {
    key: 'fingerprintRecognition',
    label: 'Recognize',
    cost: 'Free · Database',
  },
  { key: 'identify', label: 'Identify', cost: 'Paid · AI' },
];

const EMPTY_STAGES = (): Record<StepKey, StageState> => ({
  image: { status: 'complete' },
  preprocess: { status: 'idle' },
  similar: { status: 'idle' },
  text: { status: 'idle' },
  fingerprint: { status: 'idle' },
  fingerprintRecognition: { status: 'idle' },
  identify: { status: 'idle' },
});

const errorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'This recognition stage failed.';

const percentage = (value: unknown) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return `${(number <= 1 ? number * 100 : number).toFixed(1)}%`;
};

const duration = (result: any) => {
  const milliseconds =
    result?.meta?.durationMs ??
    result?.metadata?.processing_ms ??
    result?.processing_ms;
  if (!Number.isFinite(Number(milliseconds))) return null;
  return Number(milliseconds) >= 1000
    ? `${(Number(milliseconds) / 1000).toFixed(1)} sec`
    : `${Number(milliseconds)} ms`;
};

const values = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item: any) => item?.label || item?.name || String(item))
      .filter(Boolean);
  }
  return [String(value)];
};

const candidateLabel = (candidate: any) =>
  candidate?.cigarName ||
  candidate?.label ||
  candidate?.full_name ||
  candidate?.name ||
  candidate?.cigar?.full_name ||
  (candidate?.cigar_id || candidate?.cigarId
    ? `Cigar ${candidate.cigar_id || candidate.cigarId}`
    : 'Candidate');

const candidateScore = (candidate: any) =>
  percentage(
    candidate?.confidence ?? candidate?.score ?? candidate?.similarity
  );

function MatchFlowModal({ isOpen, source, onClose }: MatchFlowModalProps) {
  const [activeStep, setActiveStep] = useState<StepKey>('image');
  const [stages, setStages] = useState(EMPTY_STAGES);
  const [workingImage, setWorkingImage] = useState('');
  const [workingImageType, setWorkingImageType] = useState<
    'original' | 'manual' | 'preprocessed'
  >('original');
  const [selectedCandidates, setSelectedCandidates] = useState<
    Partial<Record<StepKey, number>>
  >({});
  const [accepted, setAccepted] = useState<any>(null);
  const sessionSource = useRef(source);

  useEffect(() => {
    if (!isOpen || !source) return;
    sessionSource.current = source;
    setActiveStep('image');
    setStages(EMPTY_STAGES());
    setWorkingImage(source.image);
    setWorkingImageType(
      source.sourceType === 'upload' && source.originalImage !== source.image
        ? 'manual'
        : 'original'
    );
    setSelectedCandidates({});
    setAccepted(null);
  }, [isOpen, source]);

  const sourceImage = source?.originalImage || source?.image || '';
  const stage = stages[activeStep];
  const activeIndex = STEPS.findIndex(({ key }) => key === activeStep);

  const clearDownstream = (step: StepKey) => {
    const start = STEPS.findIndex(({ key }) => key === step) + 1;
    setStages((current) => {
      const next = { ...current };
      STEPS.slice(start).forEach(({ key }) => {
        next[key] = { status: 'idle' };
      });
      return next;
    });
    setAccepted(null);
  };

  const selectWorkingImage = (
    image: string,
    type: 'original' | 'manual' | 'preprocessed'
  ) => {
    if (!image || image === workingImage) return;
    setWorkingImage(image);
    setWorkingImageType(type);
    clearDownstream('preprocess');
  };

  const runStage = async (key: StepKey) => {
    if (!workingImage || stages[key].status === 'processing') return;
    setActiveStep(key);
    setStages((current) => ({
      ...current,
      [key]: { ...current[key], status: 'processing', error: undefined },
    }));

    try {
      let result: any;
      if (key === 'preprocess')
        result = await preprocessCigarImage(workingImage);
      if (key === 'similar') {
        result = await findSimilarCigarsDebug(workingImage);
        result.hydratedCandidates = await hydrateSimilarityCandidates(
          result.candidates || []
        );
      }
      if (key === 'text') result = await recognizeCigarText(workingImage);
      if (key === 'fingerprint')
        result = await fingerprintCigarImage(workingImage);
      if (key === 'fingerprintRecognition') {
        const fingerprint = stages.fingerprint.result?.fingerprint;
        if (!fingerprint) throw new Error('Generate a fingerprint first.');
        result = await recognizeCigarFingerprint(fingerprint);
        result.hydratedCandidates = await hydrateCigarCandidates(
          (result.candidates || []).map((candidate: any) => ({
            ...candidate,
            cigar_id: candidate.cigar_id || candidate.cigarId,
          })),
          10
        );
      }
      if (key === 'identify') {
        result = await identifyCigarDebug(workingImage, {
          similarCandidates: stages.similar.result?.candidates,
          textRecognition: stages.text.result,
          fingerprint: stages.fingerprint.result?.fingerprint,
          fingerprintCandidates:
            stages.fingerprintRecognition.result?.candidates,
        });
        const candidates = [result.match, ...(result.suggestions || [])].filter(
          Boolean
        );
        result.hydratedCandidates = await hydrateCigarCandidates(
          candidates,
          10
        );
      }

      setStages((current) => ({
        ...current,
        [key]: { status: 'complete', result },
      }));
      if (key === 'preprocess' && workingImageType === 'preprocessed') {
        if (result.image) setWorkingImage(result.image);
        else {
          setWorkingImage(source?.image || sourceImage);
          setWorkingImageType(
            source?.image !== sourceImage ? 'manual' : 'original'
          );
        }
      }
    } catch (error) {
      setStages((current) => ({
        ...current,
        [key]: {
          ...current[key],
          status: 'failed',
          error: errorMessage(error),
        },
      }));
    }
  };

  const skipPreprocess = () => {
    setStages((current) => ({
      ...current,
      preprocess: { ...current.preprocess, status: 'skipped' },
    }));
    setActiveStep('similar');
  };

  const candidates = useMemo(() => {
    const { result } = stage;
    if (!result) return [];
    if (result.hydratedCandidates?.length) return result.hydratedCandidates;
    if (activeStep === 'text') {
      return (
        result.cigarCandidates ||
        result.cigar_candidates ||
        result.candidates ||
        []
      );
    }
    return result.candidates || [];
  }, [activeStep, stage.result]);

  const selectedCandidate = candidates[selectedCandidates[activeStep] || 0];
  const acceptCandidate = () => {
    if (!selectedCandidate) return;
    setAccepted({
      candidate: selectedCandidate,
      step: activeStep,
      confidence: candidateScore(selectedCandidate),
    });
  };

  const nextStep = () => {
    const next = STEPS[activeIndex + 1];
    if (!next) return;
    setActiveStep(next.key);
    if (stages[next.key].status === 'idle') runStage(next.key);
  };

  const renderMeta = (result: any, fallbackCost: string) => (
    <div className="match-flow-meta">
      <span>
        {result?.meta?.paidInference ? 'Paid inference' : fallbackCost}
      </span>
      {duration(result) && <span>{duration(result)}</span>}
      {(result?.meta?.provider || result?.metadata?.source) && (
        <span>{result.meta?.provider || result.metadata?.source}</span>
      )}
      {(result?.meta?.model || result?.metadata?.multimodal_model) && (
        <span>{result.meta?.model || result.metadata?.multimodal_model}</span>
      )}
    </div>
  );

  const renderCandidates = () => (
    <div className="match-flow-candidates">
      {!candidates.length && <p>No candidate identities were returned.</p>}
      {candidates.map((candidate: any, index: number) => (
        <button
          type="button"
          className={`match-flow-candidate${
            (selectedCandidates[activeStep] || 0) === index ? ' selected' : ''
          }`}
          key={`${candidate.cigar_id || candidate.cigarId || index}-${index}`}
          onClick={() =>
            setSelectedCandidates((current) => ({
              ...current,
              [activeStep]: index,
            }))
          }
        >
          {(candidate.cigarImageUrl ||
            candidate.image_url ||
            candidate.imageId) && (
            <img
              src={
                candidate.cigarImageUrl ||
                candidate.image_url ||
                candidate.imageId
              }
              alt=""
            />
          )}
          <span>
            <strong>{candidateLabel(candidate)}</strong>
            <small>
              {candidateScore(candidate) || 'Score unavailable'}
              {(candidate.brand || candidate.cigar?.brand?.name) &&
                ` · ${candidate.brand || candidate.cigar.brand.name}`}
            </small>
            {values(candidate.evidence).length > 0 && (
              <em>{values(candidate.evidence).join(' · ')}</em>
            )}
          </span>
        </button>
      ))}
    </div>
  );

  const renderFingerprint = (result: any) => {
    const fingerprint = result?.fingerprint || {};
    const fields = [
      ['Colors', fingerprint.colors],
      ['Symbols', fingerprint.symbols],
      ['Motifs', fingerprint.motifs],
      ['Objects', fingerprint.objects],
      ['Typography', fingerprint.textStyle || fingerprint.typography],
      ['Monograms', fingerprint.monograms],
      ['Shape', fingerprint.shape],
    ];
    return (
      <div className="match-flow-fingerprint">
        {fields.map(([label, value]) =>
          values(value).length ? (
            <section key={String(label)}>
              <strong>{label}</strong>
              <div>
                {values(value).map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </section>
          ) : null
        )}
        {fingerprint.layout && (
          <section>
            <strong>Layout</strong>
            <p>
              {Object.values(fingerprint.layout).filter(Boolean).join(' · ')}
            </p>
          </section>
        )}
        {(fingerprint.description || fingerprint.artwork) && (
          <section>
            <strong>Description</strong>
            <p>{fingerprint.description || fingerprint.artwork}</p>
          </section>
        )}
      </div>
    );
  };

  const renderText = (result: any) => (
    <div className="match-flow-text-result">
      <section>
        <strong>Detected text</strong>
        <pre>{result.rawText || 'No text detected'}</pre>
      </section>
      {result.normalizedText && (
        <section>
          <strong>Normalized</strong>
          <p>{result.normalizedText}</p>
        </section>
      )}
      {[
        ['Tokens', result.tokens],
        ['Brands', result.brandCandidates || result.brand_candidates],
        ['Names', result.nameCandidates || result.name_candidates],
        ['Aliases', result.aliases],
        ['Tags', result.tags || result.matchingTags],
      ].map(([label, value]) =>
        values(value).length ? (
          <section key={String(label)}>
            <strong>{label}</strong>
            <div className="match-flow-chips">
              {values(value).map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </section>
        ) : null
      )}
      {percentage(result.confidence) && (
        <p>Confidence {percentage(result.confidence)}</p>
      )}
    </div>
  );

  const renderResult = () => {
    if (stage.status === 'processing') {
      return (
        <div className="match-flow-processing">
          <Spinner />
          <strong>Processing…</strong>
          <span>Completed results remain available from the steps above.</span>
        </div>
      );
    }
    if (stage.status === 'failed') {
      return (
        <div className="match-flow-error">
          <strong>Stage failed</strong>
          <span>{stage.error}</span>
        </div>
      );
    }
    if (stage.status === 'idle') {
      return (
        <div className="match-flow-empty">
          <strong>This stage has not run.</strong>
          <span>Run it when you are ready to inspect its output.</span>
        </div>
      );
    }
    if (stage.status === 'skipped') {
      return (
        <div className="match-flow-empty">
          <strong>Preprocessing skipped</strong>
          <span>Subsequent stages use the selected original/manual image.</span>
        </div>
      );
    }
    if (activeStep === 'image') return null;
    if (activeStep === 'preprocess') {
      const { result } = stage;
      return (
        <>
          <div className="match-flow-preprocess-output">
            {result?.image ? (
              <img src={result.image} alt="Automatically isolated cigar band" />
            ) : (
              <div>No safe crop was produced.</div>
            )}
            <div>
              <strong>
                Confidence {percentage(result?.confidence) || '—'}
              </strong>
              {result?.region && (
                <p>
                  Region: {result.region.x}, {result.region.y} ·{' '}
                  {result.region.width} × {result.region.height}
                </p>
              )}
              <p>{values(result?.transformations).join(' · ')}</p>
              {values(result?.notes).map((note) => (
                <p key={note}>{note}</p>
              ))}
            </div>
          </div>
          {renderMeta(result, 'Free · Local')}{' '}
          {result?.image && (
            <Button
              color="primary"
              onClick={() => selectWorkingImage(result.image, 'preprocessed')}
            >
              Use processed image
            </Button>
          )}
        </>
      );
    }
    if (activeStep === 'text')
      return (
        <>
          {renderText(stage.result)}
          {renderCandidates()}
          {renderMeta(stage.result, 'Paid · Vision')}
        </>
      );
    if (activeStep === 'fingerprint')
      return (
        <>
          {renderFingerprint(stage.result)}
          {renderMeta(stage.result, 'Paid · Vision')}
        </>
      );
    return (
      <>
        {renderCandidates()}
        {renderMeta(stage.result, STEPS[activeIndex].cost)}
      </>
    );
  };

  if (!source) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="match-flow-dialog" fade>
      <div className="modal-header match-flow-header">
        <div>
          <h5 className="modal-title">Cigar Match Flow</h5>
          <small>
            {source.sourceType.replace('_', ' ')}
            {source.sourceId !== undefined ? ` · ${source.sourceId}` : ''}
          </small>
        </div>
        <button type="button" className="close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="match-flow-steps">
        {STEPS.map((item, index) => {
          const { status } = stages[item.key];
          return (
            <React.Fragment key={item.key}>
              <button
                type="button"
                className={`match-flow-step ${status}${item.key === activeStep ? ' active' : ''}`}
                onClick={() => setActiveStep(item.key)}
              >
                <span>
                  {status === 'processing' ? <Spinner size="sm" /> : index + 1}
                </span>
                <strong>{item.label}</strong>
                <small>{item.cost}</small>
              </button>
              {index < STEPS.length - 1 && <i>→</i>}
            </React.Fragment>
          );
        })}
      </div>
      <div className="modal-body match-flow-body">
        <aside className="match-flow-images">
          <div>
            <span>Original upload</span>
            <img src={sourceImage} alt="Original cigar" />
          </div>
          {source.image !== sourceImage && (
            <div>
              <span>Manual crop</span>
              <img src={source.image} alt="Manually cropped cigar" />
            </div>
          )}
          <div className="match-flow-working">
            <span>Working image · {workingImageType}</span>
            <img src={workingImage} alt="Current recognition input" />
          </div>
          <div className="match-flow-image-buttons">
            <Button
              size="sm"
              disabled={workingImage === sourceImage}
              onClick={() => selectWorkingImage(sourceImage, 'original')}
            >
              Use original
            </Button>
            {source.image !== sourceImage && (
              <Button
                size="sm"
                disabled={workingImage === source.image}
                onClick={() => selectWorkingImage(source.image, 'manual')}
              >
                Use manual crop
              </Button>
            )}
          </div>
        </aside>
        <main className="match-flow-content">
          <div className="match-flow-stage-heading">
            <div>
              <h5>{STEPS[activeIndex].label}</h5>
              <span>{STEPS[activeIndex].cost}</span>
            </div>
            {activeStep !== 'image' && (
              <Button
                size="sm"
                disabled={stage.status === 'processing'}
                onClick={() => {
                  clearDownstream(activeStep);
                  runStage(activeStep);
                }}
              >
                {stage.status === 'idle' ? 'Run Step' : 'Run Again'}
              </Button>
            )}
          </div>
          {activeStep === 'image' && (
            <div className="match-flow-image-start">
              <h4>Choose how to begin</h4>
              <p>
                Keep the current image selected, optionally isolate the band, or
                skip directly to embedding similarity.
              </p>
              <div>
                <Button color="primary" onClick={() => runStage('preprocess')}>
                  Run preprocessing
                </Button>
                <Button onClick={skipPreprocess}>Skip to similarity</Button>
              </div>
            </div>
          )}
          {renderResult()}
          {stage.result && (
            <details className="match-flow-raw">
              <summary>Raw response</summary>
              <pre>{JSON.stringify(stage.result, null, 2)}</pre>
            </details>
          )}
        </main>
      </div>
      <div className="modal-footer match-flow-footer">
        <div>
          {accepted ? (
            <div className="match-flow-accepted">
              <strong>Accepted · {candidateLabel(accepted.candidate)}</strong>
              <span>
                Matched at{' '}
                {STEPS.find(({ key }) => key === accepted.step)?.label}
                {accepted.confidence ? ` · ${accepted.confidence}` : ''}
              </span>
            </div>
          ) : (
            <span className="text-muted">No result accepted for this run.</span>
          )}
        </div>
        <div>
          {candidates.length > 0 && activeStep !== 'fingerprint' && (
            <Button color="success" onClick={acceptCandidate}>
              Accept Match
            </Button>
          )}
          {activeStep !== 'image' &&
            activeIndex < STEPS.length - 1 &&
            stage.status !== 'processing' && (
              <Button color="primary" onClick={nextStep}>
                Next Step
              </Button>
            )}
          <Button color="light" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default MatchFlowModal;
